import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Copy, Upload, X, FileText, Link2 } from 'lucide-react'
import RichTextEditor from './RichTextEditor'
import {
  createAdminPolicy,
  updateAdminPolicy,
} from '../../../services/adminPolicyService'

const MAX_FILE_MB = 15

export default function AdminPolicyFormModal({ isOpen, onClose, policy = null, onSaved }) {
  const isEdit = Boolean(policy?.id)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [file, setFile] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [savedLink, setSavedLink] = useState('')
  const [copied, setCopied] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return
    setName(policy?.name ?? '')
    setDescription(policy?.description ?? '')
    setBodyHtml(policy?.body_html ?? '')
    setIsActive(policy ? Boolean(policy.is_active) : true)
    setFile(null)
    setError('')
    setSavedLink('')
    setCopied(false)
  }, [isOpen, policy])

  if (!isOpen) return null

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] ?? null
    if (selected && selected.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`File must be smaller than ${MAX_FILE_MB} MB.`)
      e.target.value = ''
      return
    }
    setError('')
    setFile(selected)
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(savedLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Unable to copy the link automatically. Select it manually to copy.')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim()) {
      setError('Policy name is required.')
      return
    }
    if (!description.trim()) {
      setError('Description is required.')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        body_html: bodyHtml,
        is_active: isActive,
      }
      if (file) payload.file = file

      const result = isEdit
        ? await updateAdminPolicy(policy.id, payload)
        : await createAdminPolicy(payload)

      const link = result?.link ?? ''
      setSavedLink(link)
      onSaved?.(result)

      if (!isEdit && link) {
        // Keep the modal open on first create so the admin can copy the link.
        return
      }
      onClose?.()
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save the policy document.')
    } finally {
      setSubmitting(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 bg-gray-900/50"
      />
      <div className="relative z-10 my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-bold text-gray-900">
            {isEdit ? 'Edit policy document' : 'Create policy document'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {savedLink && !isEdit ? (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
              <Check size={16} aria-hidden /> Policy document created.
            </p>
            <p className="mt-1 text-xs text-emerald-700/80">
              Share this signing link with your staff. It is protected and only accepts @fastrapid.com addresses.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <input
                readOnly
                value={savedLink}
                onFocus={(e) => e.target.select()}
                className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs text-gray-800"
              />
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-700"
              >
                {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-gray-800"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {error ? (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Policy name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={255}
                placeholder="e.g. Code of Conduct 2026"
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rapid-orange/30"
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Description</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={2}
                maxLength={5000}
                placeholder="A short summary of what this policy covers."
                className="mt-1 w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rapid-orange/30"
              />
            </label>

            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Policy text</span>
              <div className="mt-1">
                <RichTextEditor value={bodyHtml} onChange={setBodyHtml} />
              </div>
            </div>

            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Attachment (optional PDF/Word)</span>
              {policy?.file?.file_name ? (
                <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                  <FileText size={13} aria-hidden /> Current: {policy.file.file_name}
                </p>
              ) : null}
              <div className="mt-1 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <Upload size={15} aria-hidden />
                  {file ? 'Change file' : 'Upload file'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {file ? (
                  <span className="truncate text-xs text-gray-600">{file.name}</span>
                ) : (
                  <span className="text-xs text-gray-400">PDF, DOC or DOCX · max {MAX_FILE_MB} MB</span>
                )}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-rapid-orange focus:ring-rapid-orange/30"
              />
              Active — accepting signatures
            </label>

            {isEdit ? (
              <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                <Link2 size={14} className="mt-0.5 shrink-0" aria-hidden />
                Editing the name, description, text or file bumps the version and asks everyone to sign again.
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-rapid-orange px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#E84A15] disabled:opacity-60"
              >
                {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create document'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  )
}
